import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import { IconButton } from "@mui/material";

import { SliderItem, Store } from "../../../../types";
import Notification from "../../../global/Notification";
import StickySaveBar from "../../../global/StickySaveBar";
import { SpinnerCircularFixed } from "../../../../assets/spinner/SpinnerCircularFixed";
import NoImage from "../../../../assets/NoImage.jpg";
import { BackButton } from "../../../../assets/BackButton";
import { Layout } from "../../../global/Layout";

import { useAuth } from "../../../../utils/authorization";
import { useHasScope } from "../../../../utils/Permission";
import { getStoreById } from "../../../../lib/admin";
import { CarouselSlideEditorImageForm } from "./CarouselSlideEditorImageForm";
import { CarouselSlideEditorVideoForm } from "./CarouselSlideEditorVideoForm";
import { CarouselSlideLivePreview } from "./CarouselSlideLivePreview";
import { extractYouTubeId } from "../hooks-functions/ExtractYoutubeId";
import ConfirmModal from "../../../global/ConfirmModal";
import { ensureIds, generateSlideId } from "../hooks-functions/CarouselUtils";
import { getCustomizationSection, saveCustomizationSection, uploadCustomizationImage } from '../../../../lib/customization/customizationService';

type TabKey =
    | "syncSettings"
    | "syncType"
    | "retailSystem"
    | "shopify"
    | "csv"
    | "settings"
    | "customization";

const TAB_CLASS: Record<TabKey, string> = {
    syncSettings: "SyncTab",
    syncType: "SyncTypeTab",
    retailSystem: "RetailSystemTab",
    shopify: "ShopifyTab",
    csv: "CSVTab",
    settings: "SettingsTab",
    customization: "CustomizationToolTab",
};

const makeEmptySlide = (): SliderItem => ({
    src: "",
    h2: { text: "", color: "#ffffff" },
    p: { text: "", color: "#ffffff" },
    a: "",
    url: "#",
    imageUrl: "#",
    showButton: false,
    redirectImage: false,
    logo: "",
    mobile: "",
    videoUrl: "",
    imageBrightness: 85,
    horizontalAlignment: 'center',
    verticalAlignment: 'center',
    useMobileAlignment: false,
    horizontalAlignmentMobile: 'center',
    verticalAlignmentMobile: 'center',
    buttonBackgroundColor: '#000000',
    buttonTextColor: '#ffffff',
});

const normalizeSliderItem = (item: SliderItem): SliderItem => {
    const copy: SliderItem = { ...item };
    const hasImageUrl = !!copy?.imageUrl && copy?.imageUrl !== "#";

    if (copy.imageBrightness === undefined) {
        copy.imageBrightness = 85;
    }

    if (copy?.showButton) {
        copy.imageUrl = "";
        (copy as any).imageUrlEnabled = false;
    } else if (hasImageUrl) {
        copy.a = "";
        copy.url = "";
        copy.showButton = false;
        (copy as any).imageUrlEnabled = true;
    } else {
        (copy as any).imageUrlEnabled = false;
    }

    return copy;
};

export default function CarouselSlideEditorContainer() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ storeId: string; slideIndex?: string }>();
    const storeId = params.storeId;
    const isNew = !params.slideIndex || params.slideIndex === "new";
    const slideIdFromUrl = params.slideIndex;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState<{
        message: string | null;
        type: "success" | "error";
    }>({ message: null, type: "success" });
    const [saveError, setSaveError] = useState<string | null>(null);

    const [sliderOn, setSliderOn] = useState<boolean>(false);
    const [interval, setInterval] = useState<number>(0);
    const [items, setItems] = useState<SliderItem[]>([]);
    const [effectiveIndex, setEffectiveIndex] = useState<number>(-1);
    const [openMobileMenu, setOpenMobileMenu] = useState(false);
    const [uploadingImages, setUploadingImages] = useState<{
        [key: number]: { [key: string]: boolean };
    }>({});
    const [originalData, setOriginalData] = useState<{
        sliderOn: boolean;
        interval: number;
        items: SliderItem[];
    } | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [initialItemsBeforeEdit, setInitialItemsBeforeEdit] = useState<SliderItem[]>([]);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
    const [store, setStore] = useState<Store | undefined>(undefined);
    const [storeLoading, setStoreLoading] = useState(true);

    const { user } = useAuth();
    const isLoggedIn = !!user;

    const hasUpdateStoreSync = useHasScope("update:storesyncsettings") || user?.stores?.length === 1;
    const hasUpdateSyncType = useHasScope("update:synctype");
    const hasUpdateShopifyInfo = useHasScope("update:shopifyinfo");
    const hasUpdateRSInformation = useHasScope("update:rsinformation");
    const hasUpdateJsonSettings = useHasScope("update:jsonsettings");
    const hasUpdateCsvConfiguration = useHasScope("update:csvconfiguration");
    const [slideMode, setSlideMode] = useState<"image" | "video">("image");
    const [showModeWarning, setShowModeWarning] = useState<"image" | "video" | null>(null);
    const [previewViewMode, setPreviewViewMode] = useState<"desktop" | "mobile">("desktop");

    const availableTabs: { key: TabKey; label: string }[] = [
        ...(hasUpdateStoreSync ? [{ key: "syncSettings" as const, label: "Sync Settings" }] : []),
        ...(hasUpdateSyncType ? [{ key: "syncType" as const, label: "Sync Type" }] : []),
        ...(hasUpdateRSInformation ? [{ key: "retailSystem" as const, label: "RetailSystem" }] : []),
        ...(hasUpdateShopifyInfo ? [{ key: "shopify" as const, label: "Shopify" }] : []),
        ...(hasUpdateCsvConfiguration ? [{ key: "csv" as const, label: "CSV" }] : []),
        ...(hasUpdateJsonSettings ? [{ key: "settings" as const, label: "Settings" }] : []),
        { key: "customization" as const, label: "Customization" },
    ];

    const activeSidebarTab: TabKey = "customization";

    const closeNotification = useCallback(() => setNotification({ message: null, type: "success" }), []);
    const dismissSaveError = useCallback(() => setSaveError(null), []);

    const toggleMenu = (state: boolean) => () => {
        setOpenMobileMenu(state);
    };

    const navigateToCarousel = (successMessage?: string) => {
        navigate(
            `/management/${storeId}?tab=customization&section=carousel`,
            successMessage ? { state: { successMessage } } : undefined
        );
    };

    const hasFormChanges = JSON.stringify(items) !== JSON.stringify(initialItemsBeforeEdit);

    const runWithUnsavedConfirmation = (action: () => void) => {
        if (hasSlideChanges) {
            setPendingNavigation(() => action);
            setShowUnsavedModal(true);
        } else {
            action();
        }
    };

    const handleBack = () => {
        if (effectiveIndex < 0 || effectiveIndex >= items.length) {
            navigateToCarousel();
            return;
        }

        const currentItem = items[effectiveIndex];
        const isEmpty = !slideHasImageContent(currentItem) && !slideHasVideoContent(currentItem);

        if (isNew && isEmpty) {
            navigateToCarousel();
            return;
        }

        runWithUnsavedConfirmation(() => navigateToCarousel());
    };

    const handleDiscardAndExit = () => {
        setShowUnsavedModal(false);
        const action = pendingNavigation;
        setPendingNavigation(null);
        if (action) {
            action();
        } else {
            navigateToCarousel();
        }
    };

    const handleCloseUnsavedModal = () => {
        setShowUnsavedModal(false);
        setPendingNavigation(null);
    };

    const navigateToTab = (tabKey: TabKey) => {
        if (!storeId) return;

        if (tabKey === "customization") {
            handleBack();
            return;
        }

        runWithUnsavedConfirmation(() => navigate(`/management/${storeId}?tab=${tabKey}`));
    };

    const checkForChanges = (next: {
        sliderOn: boolean;
        interval: number;
        items: SliderItem[];
    }, base?: typeof originalData) => {
        const compareBase = base || originalData;
        if (!compareBase) return false;
        return (
            next.sliderOn !== compareBase.sliderOn ||
            next.interval !== compareBase.interval ||
            JSON.stringify(next.items) !== JSON.stringify(compareBase.items)
        );
    };

    useEffect(() => {
        setLoading(true);
        if (!storeId) return;

        getCustomizationSection<any>(storeId, 'HomepageCarousel')
            .then((data) => {
                const itemsFromApi: SliderItem[] = Array.isArray(data.items)
                    ? (data.items as SliderItem[])
                    : [];

                const normalizedItemsApi = ensureIds(itemsFromApi.map(it => normalizeSliderItem(it)));

                const dbOriginal = {
                    sliderOn: !!data.sliderOn,
                    interval: data.interval || 0,
                    items: normalizedItemsApi.map((it: SliderItem) => ({ ...it })),
                };
                setOriginalData(dbOriginal);

                setItems(dbOriginal.items);
                setInitialItemsBeforeEdit([...dbOriginal.items]);
                setInterval(dbOriginal.interval);
                setHasChanges(false);

                if (!isNew && slideIdFromUrl) {
                    let idx = normalizedItemsApi.findIndex(it => it.id === slideIdFromUrl);
                    
                    if (idx === -1 && !isNaN(Number(slideIdFromUrl))) {
                        idx = Number(slideIdFromUrl);
                    }

                    setEffectiveIndex(idx);
                }
            })
            .finally(() => setLoading(false));
    }, [storeId, isNew, slideIdFromUrl]);

    const lastInitializedIndex = useRef<number | null>(null);

    useEffect(() => {
        if (loading) return;
        
        let idx = -1;
        if (isNew) {
            idx = items.length - 1;
        } else {
            idx = items.findIndex(it => it.id === slideIdFromUrl);

            if (idx === -1 && slideIdFromUrl && !isNaN(Number(slideIdFromUrl))) {
                idx = Number(slideIdFromUrl);
            }
        }
        
        setEffectiveIndex(idx);

        if (idx >= 0 && idx < items.length && lastInitializedIndex.current !== idx) {
            lastInitializedIndex.current = idx;
            const currentItem = items[idx];
            if ((currentItem as any)?.videoUrl && (currentItem as any).videoUrl.trim()) {
                setSlideMode("video");
            } else {
                setSlideMode("image");
            }
        }
    }, [loading, isNew, slideIdFromUrl, items]);

    const handleModeSwitch = (targetMode: "image" | "video") => {
        if (targetMode === slideMode) return;

        if (effectiveIndex < 0 || effectiveIndex >= items.length) return;
        const currentItem = items[effectiveIndex];

        const hasContent =
            targetMode === "video"
                ? slideHasImageContent(currentItem)
                : slideHasVideoContent(currentItem);

        if (hasContent) {
            setShowModeWarning(targetMode);
        } else {
            applyModeSwitch(targetMode);
        }
    };



    const applyModeSwitch = (targetMode: "image" | "video") => {
        if (effectiveIndex < 0 || effectiveIndex >= items.length) return;
        const next = [...items];

        const oldId = next[effectiveIndex].id;

        if (targetMode === "video") {
            next[effectiveIndex] = {
                ...makeEmptySlide(),
                id: oldId,
                videoUrl: (next[effectiveIndex] as any)?.videoUrl || "",
            };
        } else {
            next[effectiveIndex] = {
                ...makeEmptySlide(),
                id: oldId,
            };
        }

        setItems(next);
        setSlideMode(targetMode);
        setShowModeWarning(null);
        setHasChanges(
            checkForChanges({
                sliderOn,
                interval,
                items: next,
            })
        );
    };

    useEffect(() => {
        const fetchStore = async () => {
            if (!storeId) {
                setStore(undefined);
                setStoreLoading(false);
                return;
            }
            setStoreLoading(true);
            try {
                const newStore = await getStoreById(parseInt(storeId, 10));
                setStore(newStore);
            } catch (err) {
                console.error("Error fetching store:", err);
            } finally {
                setStoreLoading(false);
            }
        };
        fetchStore();
    }, [storeId]);

    const createdOnceRef = useRef(false);
    useEffect(() => {
        if (loading) return;
        if (!isNew) return;
        if (createdOnceRef.current) return;
        createdOnceRef.current = true;

        setItems(prev => [...prev, { ...makeEmptySlide(), id: generateSlideId() }]);
    }, [loading, isNew]);

    const uploadingRef = useRef(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, i: number) => {
        if (i < 0) return;
        const { id, value } = e.target;
        const next = [...items];
        const updatedItem: SliderItem = { ...next[i] };

        if (id === "imageUrl") {
            updatedItem.imageUrl = value;
            const hasValue = value.trim().length > 0;
            updatedItem.imageUrlEnabled = hasValue;
            if (hasValue) {
                updatedItem.a = "";
                updatedItem.url = "#";
                updatedItem.showButton = false;
            }
        } else if (id === "imageUrlEnabled") {
            updatedItem.imageUrlEnabled = value as unknown as boolean;
        } else if (id === "a" || id === "url") {
            (updatedItem as any)[id] = value;
            const hasValue = value.trim().length > 0;

            if (hasValue) {
                updatedItem.imageUrl = "";
                updatedItem.imageUrlEnabled = false;
                updatedItem.showButton = true;
            } else {
                const otherField = id === "a" ? updatedItem?.url : updatedItem?.a;
                const otherHasValue = (otherField || "").trim().length > 0;
                if (!otherHasValue) {
                    updatedItem.showButton = false;
                }
            }
        } else if (id.includes(".")) {
            const [field, sub] = id.split(".") as ["h2" | "p", "text" | "color"];
            updatedItem[field] = {
                ...(updatedItem as any)[field],
                [sub]: value,
            };
        } else {
            (updatedItem as any)[id] = value;
        }

        next[i] = updatedItem;
        setItems(next);
        setHasChanges(checkForChanges({ sliderOn, interval, items: next }));
    };

    const handleShowButtonOnChange = (state: boolean, i: number) => {
        if (i < 0) return;
        const next = [...items];
        next[i] = { ...next[i], showButton: state };
        setItems(next);
        setHasChanges(checkForChanges({ sliderOn, interval, items: next }));
    };

    const handleImage = async (e: React.ChangeEvent<HTMLInputElement>, i: number) => {
        if (i < 0) return;
        if (uploadingRef.current) return;
        uploadingRef.current = true;

        const file = e.target.files?.[0];
        if (!file) {
            uploadingRef.current = false;
            return;
        }

        const fieldMap: Record<string, keyof SliderItem> = {
            logo: "logo",
            src: "src",
            mobile: "mobile",
        };
        const fieldKey = Object.keys(fieldMap).find((k) => e.target.id.startsWith(k)) || "src";
        const field = fieldMap[fieldKey];

        const MAX_FILE_SIZE = 1024 * 1024;
        const IMAGE_DIMENSIONS: Record<string, { width: number }> = {
            mobile: { width: 1024 },
            logo: { width: 250 },
            src: { width: 1920 },
        };

        if (file.size > MAX_FILE_SIZE) {
            setNotification({
                message: `File size exceeds ${MAX_FILE_SIZE / 1024 / 1024} MB.`,
                type: "error",
            });
            uploadingRef.current = false;
            return;
        }

        setUploadingImages((prev) => ({
            ...prev,
            [i]: { ...(prev[i] || {}), [field]: true },
        }));

        const image = new Image();
        const reader = new FileReader();

        reader.onload = (ev) => {
            if (!ev.target?.result) return;
            image.src = ev.target.result as string;
            image.onload = async () => {
                const expected = IMAGE_DIMENSIONS[field]?.width || 0;
                if (expected && image.width !== expected) {
                    setNotification({
                        message: `The image width for '${field}' must be exactly ${expected}px.`,
                        type: "error",
                    });
                    uploadingRef.current = false;
                    setUploadingImages((prev) => ({
                        ...prev,
                        [i]: { ...(prev[i] || {}), [field]: false },
                    }));
                    return;
                }

                const clean = file.name.replace(/[^\w.-]/g, "_");
                const sanitized = new File([file], clean, { type: file.type });

                try {
                    const url = await uploadCustomizationImage(storeId || '', sanitized);

                    const next = [...items];
                    const updatedItem = { ...next[i], [field]: url };
                    next[i] = updatedItem as SliderItem;
                    setItems(next);

                    setHasChanges(
                        checkForChanges({
                            sliderOn,
                            interval,
                            items: next,
                        })
                    );
                } catch (err: any) {
                    setNotification({
                        message: `Image upload failed: ${err.message || "Unknown error"}`,
                        type: "error",
                    });
                } finally {
                    uploadingRef.current = false;
                    setUploadingImages((prev) => ({
                        ...prev,
                        [i]: { ...(prev[i] || {}), [field]: false },
                    }));
                }
            };
        };

        reader.readAsDataURL(file);
    };

    const slideHasImageContent = (item: SliderItem): boolean => {
        return !!(
            item?.src ||
            item?.mobile ||
            (item?.imageUrl && item.imageUrl !== "#") ||
            item?.logo ||
            item?.h2?.text ||
            item?.p?.text ||
            (item?.a && item.a.trim()) ||
            (item?.url && item.url !== "#" && item.url.trim())
        );
    };

    const slideHasVideoContent = (item: SliderItem): boolean => {
        return !!((item as any)?.videoUrl && (item as any).videoUrl.trim());
    };

    const hasSlideChanges = isNew
        ? items.slice(initialItemsBeforeEdit.length).some(item => slideHasImageContent(item) || slideHasVideoContent(item))
        : hasFormChanges;

    const isUploading = Object.values(uploadingImages[effectiveIndex] || {}).some(Boolean);

    const handleCancel = () => {
        if (effectiveIndex < 0 || effectiveIndex >= items.length) return;

        const currentId = items[effectiveIndex].id;

        if (isNew) {
            const next = [...items];
            next[effectiveIndex] = { ...makeEmptySlide(), id: currentId };
            setItems(next);
            setHasChanges(checkForChanges({ sliderOn, interval, items: next }));
        } else {
            const originalSlide = initialItemsBeforeEdit.find(it => it.id === currentId);

            if (originalSlide) {
                const next = [...items];
                next[effectiveIndex] = { ...originalSlide };
                setItems(next);
                setHasChanges(checkForChanges({ sliderOn, interval, items: next }));
            }
        }
        setSaveError(null);
    };

    const saveChangesAndExit = async () => {
        if (!hasChanges) {
            setSaveError("No changes detected.");
            return;
        }

        if (!items.length) {
            setSaveError("You must add at least one slide before saving.");
            return;
        }

        for (const item of items) {
            const hasVideo = !!((item as any)?.videoUrl && (item as any).videoUrl.trim());
            const hasDesktop = !!(item?.src && item.src.trim());

            if (!hasVideo && !hasDesktop) {
                setSaveError("Each slide needs either a desktop image or a video URL.");
                return;
            }

            if (hasVideo && !extractYouTubeId((item as any).videoUrl)) {
                setSaveError("Invalid YouTube URL. Please use a valid YouTube link.");
                return;
            }
        }

        try {
            setSaving(true);
            setSaveError(null);
            const body = {
                sliderOn, 
                interval, 
                items, 
            };
            await saveCustomizationSection({
                storeId: storeId || '',
                metafield: 'HomepageCarousel',
                section: 'carousel',
                payload: body,
                lastValue: originalData,
            });

            setOriginalData({
                sliderOn,
                interval,
                items: items.map((it) => ({ ...it })),
            });
            setHasChanges(false);
            setShowUnsavedModal(false);
            const nextAction = pendingNavigation;
            setPendingNavigation(null);
            if (nextAction) {
                nextAction();
            } else {
                navigateToCarousel("Changes saved.");
            }
        } catch (error) {
            console.error("Error saving carousel slide:", error);
            setSaveError("Failed to save changes. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const renderStoreLogo = () => {
        if (storeLoading) return <SpinnerCircularFixed size={24} />;
        if (store?.logo && store.logo.toLowerCase() !== "x") {
            return (
                <img
                    src={store.logo}
                    alt={`logo-${store.friendlyName}`}
                    id="logo-store"
                    className="logo-store-edit-section"
                />
            );
        }
        return <img src={NoImage} alt="No image" className="logo-store-edit-section" />;
    };

    const renderSidebar = () => (
        <div className="leftVertical">
            <div className="logo-title-store">
                <div className="one-col storeLogo">{renderStoreLogo()}</div>
                <div className="title-site">
                    <h6>{store?.friendlyName}</h6>
                </div>
            </div>
            <Tabs
                orientation="vertical"
                variant="scrollable"
                aria-label="Vertical tabs"
                sx={{ borderRight: 1, borderColor: "divider" }}
                value={activeSidebarTab}
            >
                {availableTabs.map((t) => (
                    <Tab
                        key={t.key}
                        className={TAB_CLASS[t.key]}
                        label={t.label}
                        value={t.key}
                        onClick={() => navigateToTab(t.key)}
                    />
                ))}
            </Tabs>
        </div>
    );

    if (loading) {
        return (
            <div className="loading-div">
                <SpinnerCircularFixed />
            </div>
        );
    }

    if (!isNew && (effectiveIndex < 0 || effectiveIndex >= items.length)) {
        return (
            <Layout>
                <div className="editStores">
                    <Box
                        sx={{
                            flexGrow: 1,
                            bgcolor: "background.paper",
                            display: "flex",
                            borderRadius: "15px",
                        }}
                    >
                        {renderSidebar()}
                        <div className="dataPanel">
                            <div className="editorPageHeader">
                                <button className="backBtn" onClick={handleBack}>
                                    <BackButton /> Back
                                </button>
                            </div>
                            <div className="alert alert-warning">Slide not found.</div>
                        </div>
                    </Box>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="editStores">
                <div className="mobile-side-menu">
                    <IconButton onClick={toggleMenu(true)}>
                        <MenuIcon />
                    </IconButton>
                    {isLoggedIn && (
                        <button className="backBtn" onClick={handleBack}>
                            <BackButton color="black" /> Back
                        </button>
                    )}
                </div>

                <Drawer open={openMobileMenu} onClose={toggleMenu(false)}>
                    <div className="mobile-drawer-content">
                        <button className="backBtn" onClick={() => setOpenMobileMenu(false)}>
                            <BackButton color="black" />
                        </button>
                        <div className="logo-title">
                            {store?.logo && store.logo.toLowerCase() !== "x" ? (
                                <img
                                    src={store.logo}
                                    alt={`logo-${store.friendlyName}`}
                                    className="mobile-drawer-logo"
                                />
                            ) : (
                                <img src={NoImage} alt="No image" className="logo-store-edit-section" />
                            )}
                            <h4 className="mobile-drawer-title">{store?.friendlyName}</h4>
                        </div>

                        {availableTabs.map((t) => (
                            <div
                                key={t.key}
                                className={`mobile-tab-item ${TAB_CLASS[t.key]} ${activeSidebarTab === t.key ? "active" : ""}`}
                                onClick={() => {
                                    navigateToTab(t.key);
                                    setOpenMobileMenu(false);
                                }}
                            >
                                {t.label}
                            </div>
                        ))}
                    </div>
                </Drawer>

                <ConfirmModal
                    open={showUnsavedModal}
                    title="Unsaved changes"
                    message="You have unsaved changes. Would you like to save them before leaving?"
                    onCancel={handleDiscardAndExit}
                    onConfirm={saveChangesAndExit}
                    onClose={handleCloseUnsavedModal}
                    cancelLabel="Discard"
                    confirmLabel="Save"
                    confirmColor="primary"
                />

                <ConfirmModal
                    open={!!showModeWarning}
                    title={`Switch to ${showModeWarning} mode?`}
                    message={`Switching to ${showModeWarning} mode will erase the current ${showModeWarning === "video" ? "image" : "video"} content. This action cannot be undone.`}
                    onCancel={() => setShowModeWarning(null)}
                    onConfirm={() => showModeWarning && applyModeSwitch(showModeWarning)}
                />

                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: "background.paper",
                        display: "flex",
                        borderRadius: "15px",
                    }}
                >
                    {renderSidebar()}

                    <div className="dataPanel">
                        <div className="editorPageHeader">
                            <h5>{isNew ? "New slide" : "Edit slide"}</h5>
                            <button className="backBtn" onClick={handleBack}>
                                <BackButton color="black" /> Back
                            </button>
                        </div>

                        <div className="editorModeToggle">
                            <span>Select Slide Type:</span>
                            <button
                                className={`btn ${slideMode === "image" ? "btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => handleModeSwitch("image")}
                            >
                                Image
                            </button>
                            <button
                                className={`btn ${slideMode === "video" ? "btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => handleModeSwitch("video")}
                            >
                                Video
                            </button>
                        </div>

                        {effectiveIndex >= 0 && effectiveIndex < items.length && (
                            <>
                                {slideMode === "image" ? (
                                    <CarouselSlideEditorImageForm
                                        item={items[effectiveIndex]}
                                        index={effectiveIndex}
                                        isRestricted={false}
                                        uploadingImages={uploadingImages[effectiveIndex] || {}}
                                        onImageUpload={(e) => handleImage(e, effectiveIndex)}
                                        onFieldChange={(id, value) =>
                                            handleChange({ target: { id, value } } as any, effectiveIndex)
                                        }
                                        onShowButtonChange={(enabled) =>
                                            handleShowButtonOnChange(enabled, effectiveIndex)
                                        }
                                        onPreviewViewModeChange={setPreviewViewMode}
                                    />
                                ) : (
                                    <CarouselSlideEditorVideoForm
                                        videoUrl={(items[effectiveIndex] as any)?.videoUrl || ""}
                                        isRestricted={false}
                                        onFieldChange={(id, value) =>
                                            handleChange({ target: { id, value } } as any, effectiveIndex)
                                        }
                                    />
                                )}

                                {items[effectiveIndex] && (
                                    <CarouselSlideLivePreview
                                        items={items}
                                        currentIndex={effectiveIndex}
                                        isVideoSlide={slideMode === "video"}
                                        viewMode={previewViewMode}
                                        onViewModeChange={setPreviewViewMode}
                                    />
                                )}
                            </>
                        )}

                        <StickySaveBar
                            visible={hasSlideChanges}
                            saving={saving}
                            disabled={isUploading}
                            onSave={saveChangesAndExit}
                            onCancel={handleCancel}
                            alignWithSelector=".leftVertical"
                            errorMessage={saveError ?? undefined}
                            onErrorDismiss={dismissSaveError}
                        />
                        <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
                    </div>
                </Box>
            </div>
        </Layout>
    );
}
